import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getEngagementLetter, respondToEngagementLetter, fetchEngagementLetterVersions } from '../../store/actions/Drafts/EngagementLetterActions';
import { RootState, useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import apis from '../../utils/apis';
import DynamicEditor from '../Cases/Editor/DynamicEditor';
import { showModal } from '../../store/actions/modalActions';
import { toErrorText } from '../../utils/errorMessage';

interface ClientEngagementTermsProps {
  lawyerId: string;
}

interface EngagementLetterData {
  LetterID: string;
  Version: number;
  Content: string;
  HTMLContent: string;
  Status: string;
  ClientResponse: string;
  ClientComment: string;
  message?: string;
}

const isEngagementLetterData = (response: any): response is EngagementLetterData => {
  return response && typeof response === 'object' && 'LetterID' in response && 'Version' in response;
};

const isErrorMessage = (response: any): response is { message: unknown } => {
  return response && typeof response === 'object' && 'message' in response;
};

const ClientEngagementTerms: React.FC<ClientEngagementTermsProps> = ({ lawyerId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [letter, setLetter] = useState<EngagementLetterData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);

  const userId = useSelector((state: RootState) => state.user.userInfo.UserID);

  const fetchLatestLetter = async () => {
    setIsLoading(true);
    try {
      const letterId = `${lawyerId}_${userId}`;
      const fetchedVersions = await dispatch(fetchEngagementLetterVersions(userId)); 
      if (fetchedVersions && fetchedVersions.length > 0) {
        const latestVersion = fetchedVersions[0]; 
        setLetter(latestVersion);
      } else {
        const response = await dispatch(getEngagementLetter(letterId));
        if (isEngagementLetterData(response)) {
          setLetter(response);
        } else if (isErrorMessage(response)) {
          setError(toErrorText(response.message) || t('No engagement terms available'));
        } else {
          setError(t('No engagement terms available'));
        }
      }
    } catch (err) {
      setError(t('Failed to fetch engagement terms'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestLetter();
  }, [lawyerId, userId, dispatch, t]);

  const handleRespond = async (response: 'Accepted' | 'Declined', comment?: string, signatureFile?: File) => {
    if (!letter) return;
    try {
      await dispatch(
        respondToEngagementLetter(
          letter.LetterID,
          letter.Version,
          response,
          comment,
          signatureFile
        )
      );
      await fetchLatestLetter();
      dispatch(
        showFeedbackModal({
          modalType: 'success',
          message: t(`Engagement letter ${response.toLowerCase()}`),
          showModal: true,
          duration: 3000,
        })
      );
    } catch (err) {
      if(!err) return
      dispatch(
        showFeedbackModal({
          modalType: 'error',
          message: t('Failed to respond to engagement letter'),
          showModal: true,
          duration: 3000,
        })
      );
    }
  };

  const handleNotifyLawyer = async () => {
    try {
      const response = await apis.post('/engagement_letter/notify_lawyer', { lawyer_id: lawyerId });
      if (response.status === 200) {
        dispatch(
          showFeedbackModal({
            modalType: 'success',
            message: t('Notification sent to the lawyer'),
            showModal: true,
            duration: 3000,
          })
        );
      } else {
        throw new Error('Failed to notify lawyer');
      }
    } catch (err) {
      if(!err) return
      dispatch(
        showFeedbackModal({
          modalType: 'error',
          message: t('Failed to send notification'),
          showModal: true,
          duration: 3000,
        })
      );
    }
  };

  const handleDecline = () => {
    dispatch(
      showModal({
        type: 'declineEngagement',
        message: t('Decline engagement letter'),
        subMsg: t('Please provide a reason for declining. This will be sent to the lawyer.'),
        onConfirm: (comment: string) => {
          handleRespond('Declined', comment);
        },
        showModal: true,
      })
    );
  };

  const handleAccept = () => {
    dispatch(
      showModal({
        type: 'signEngagement',
        message: '',
        onConfirm: (signatureFile: File) => {
          handleRespond('Accepted', undefined, signatureFile);
        },
        showModal: true,
      })
    );
  };

  if (isLoading) {
    return (
      <Skeleton variant="prose" rows={4} />
    );
  }

  if (error) {
    return (
      <div className="text-gray-500">
        <p>{error}</p>
        <Button variant="secondary" size="small" onClick={handleNotifyLawyer} className="mt-2">
          {t('Notify lawyer')}
        </Button>
      </div>
    );
  }

  if (!letter || letter.Status !== 'Final') {
    return (
      <div className="text-gray-500">
        <p>{t('No engagement terms available. The lawyer will set them as soon as possible for you to review.')}</p>
        <Button variant="secondary" size="small" onClick={handleNotifyLawyer} className="mt-2">
          {t('Notify lawyer')}
        </Button>
      </div>
    );
  }

  return (
    <div className=''>
    <DynamicEditor
      editorRef={editorRef}
      contentType={t('Engagement terms')}
      matterId={lawyerId}
      content={letter.HTMLContent || letter.Content}
      onSave={() => {}}
      isFullscreen={isFullscreen}
      setIsFullscreen={setIsFullscreen}
      isClient={true}
      onAccept={handleAccept}
      onDecline={handleDecline}
      selectedVersionStatus={letter.Status}
      clientResponse={letter.ClientResponse}
    />
    </div>
  );
};

export default ClientEngagementTerms;
